/**
 * Hook for slide-out panel behavior management
 *
 * Encapsulates the common animation, keyboard, focus trap, and scroll lock
 * logic used by slide-out card details panels across features.
 *
 * Usage:
 * ```tsx
 * const { shouldRender, isAnimating, panelRef, handleClose, handleBackdropClick } =
 *   useSlideOutPanel({
 *     isOpen: selectedCard !== null,
 *     onClose: clearSelection,
 *   });
 * ```
 */
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSlideOutPanelOptions {
  /** Whether the panel should be open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Animation duration in ms (default: 200) */
  animationDuration?: number;
}

export interface UseSlideOutPanelReturn {
  /** Whether the panel should be rendered in the DOM */
  shouldRender: boolean;
  /** Whether the panel should be animated to open position */
  isAnimating: boolean;
  /** Ref to attach to the panel element for focus trap */
  panelRef: RefObject<HTMLDivElement | null>;
  /** Call this to close the panel with animation */
  handleClose: () => void;
  /** Backdrop click handler - closes panel if clicking outside */
  handleBackdropClick: (e: React.MouseEvent) => void;
}

/**
 * Hook for managing slide-out panel behavior.
 *
 * Provides:
 * - Enter/exit animation state management
 * - Escape key handling
 * - Body scroll lock when open
 * - Focus trap within panel
 * - Backdrop click handling
 */
export function useSlideOutPanel({ isOpen, onClose, animationDuration = 200 }: UseSlideOutPanelOptions): UseSlideOutPanelReturn {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle open/close animation states
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimating(true));
      });
    } else {
      setIsAnimating(false);
      // Wait for exit animation before unmounting
      const timer = setTimeout(() => setShouldRender(false), animationDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, animationDuration]);

  // Handle escape key
  useEffect(() => {
    if (!shouldRender) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shouldRender, onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (shouldRender) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldRender]);

  // Focus trap
  useEffect(() => {
    if (!shouldRender || !panelRef.current) return;

    const panel = panelRef.current;
    const focusableElements = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    firstElement?.focus();

    return () => document.removeEventListener('keydown', handleTab);
  }, [shouldRender, isOpen]); // Re-run when isOpen changes to handle content updates

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  return {
    shouldRender,
    isAnimating,
    panelRef,
    handleClose: onClose,
    handleBackdropClick,
  };
}

export default useSlideOutPanel;
